import { NewRenterHandbook } from "@/app/components/handbooks/renterHandbook";
import { CodependentRenterRequisitesHandbook } from "@/app/components/handbooks/renterRequisitesHandbook";
import { NewRequisitesHandbook } from "@/app/components/handbooks/requisitesHandbook";
import { parseDate } from "@internationalized/date";
import { Controller, useForm } from "react-hook-form";
import { Input, DatePicker, Button, Checkbox } from "@heroui/react";
import { I18nProvider } from "@react-aria/i18n";
import { useEffect, useState } from "react";
import { ModalError } from "../modals/modalBuilder";
import { normalizeDate } from "@/utils";

interface InvitationFormProps {
  defaultValues?: Partial<InvitationFormData>;
  onSubmit: (data: InvitationFormData) => void;
  isEdit?: boolean;
  modalErrors?: ModalError[];
  type: "existing_renter" | "new_renter";
  onDelete?: () => void;
  createNewRequisite?: boolean;
  onCreateRequisiteToggle?: (value: boolean) => void;
}

export interface InvitationFormData {
  startDate: string;
  endDate: string;
  meetingHours: number;
  renter?: number;
  renterRequisite?: number;
  requisite: number;
  type: "existing_renter" | "new_renter";
  number: string;
}

export function InvitationForm({
  defaultValues,
  onSubmit,
  isEdit = false,
  modalErrors = [],
  type,
  onDelete,
  createNewRequisite = false,
  onCreateRequisiteToggle,
}: InvitationFormProps) {
  const [createNewRequisiteState, setCreateNewRequisiteState] =
    useState(createNewRequisite);

  const form = useForm<InvitationFormData>({
    defaultValues: {
      type,
      ...defaultValues,
    },
  });

  useEffect(() => {
    setCreateNewRequisiteState(createNewRequisite);
  }, [createNewRequisite]);

  useEffect(() => {
    if (modalErrors.length) {
      modalErrors.forEach(({ field, message }) => {
        form.setError(field as keyof InvitationFormData, {
          type: "manual",
          message,
        });
      });
    } else {
      form.clearErrors();
    }
  }, [modalErrors, form]);

  useEffect(() => {
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        if (value === undefined) return;

        if (key === "start_date" || key === "end_date") {
          form.setValue(
            key as keyof InvitationFormData,
            normalizeDate(value as string),
          );
        } else {
          form.setValue(key as keyof InvitationFormData, value as never);
        }
      });
    }
  }, [defaultValues, form]);

  const handleCreateRequisiteToggle = (checked: boolean) => {
    setCreateNewRequisiteState(checked);
    if (onCreateRequisiteToggle) {
      onCreateRequisiteToggle(checked);
    }
    if (checked) {
      form.setValue("renterRequisite", undefined);
      form.clearErrors("renterRequisite");
    }
  };

  return (
    <form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4 md:w-1/2">
        {type === "existing_renter" && (
          <>
            <Controller
              control={form.control}
              name="renter"
              rules={{ required: true }}
              render={({ field }) => (
                <NewRenterHandbook
                  isRequired
                  isInvalid={!!form.formState.errors["renter"]}
                  errorMessage={
                    form.formState.errors["renter"]?.message?.toString() || ""
                  }
                  label="Арендатор"
                  variant="bordered"
                  selectedKey={String(field.value || "")}
                  onSelectionChange={(key) => field.onChange(Number(key))}
                  children={<></>}
                />
              )}
            />

            <Checkbox
              isSelected={createNewRequisiteState}
              onValueChange={handleCreateRequisiteToggle}>
              Создать новый реквизит для арендатора
            </Checkbox>

            {!createNewRequisiteState && (
              <Controller
                control={form.control}
                name="renterRequisite"
                rules={{ required: true }}
                render={({ field }) => (
                  <CodependentRenterRequisitesHandbook
                    isRequired
                    isInvalid={!!form.formState.errors["renterRequisite"]}
                    errorMessage={
                      form.formState.errors[
                        "renterRequisite"
                      ]?.message?.toString() || ""
                    }
                    label="Реквизиты арендатора"
                    variant="bordered"
                    selectedKey={String(field.value || "")}
                    onSelectionChange={(key) => field.onChange(Number(key))}
                    deps={[String(form.watch("renter") || "0")]}
                    children={<></>}
                  />
                )}
              />
            )}
          </>
        )}

        <Controller
          control={form.control}
          name="requisite"
          rules={{ required: true }}
          render={({ field }) => (
            <NewRequisitesHandbook
              isRequired
              isInvalid={!!form.formState.errors["requisite"]}
              errorMessage={
                form.formState.errors["requisite"]?.message?.toString() || ""
              }
              label="Бизнес-реквизиты"
              variant="bordered"
              selectedKey={String(field.value || "")}
              onSelectionChange={(key) => field.onChange(Number(key))}
              children={<></>}
            />
          )}
        />

        <Controller
          control={form.control}
          name="number"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              {...field}
              isRequired
              isInvalid={!!form.formState.errors["number"]}
              errorMessage={
                form.formState.errors["number"]?.message?.toString() || ""
              }
              label="Номер договора"
              variant="bordered"
            />
          )}
        />

        <Controller
          control={form.control}
          name="meetingHours"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              {...field}
              isRequired
              isInvalid={!!form.formState.errors["meetingHours"]}
              errorMessage={
                form.formState.errors["meetingHours"]?.message?.toString() || ""
              }
              label="Часы на переговорку"
              variant="bordered"
              type="number"
              value={field.value?.toString()}
              onChange={(e) => field.onChange(Number(e.target.value))}
            />
          )}
        />

        <Controller
          control={form.control}
          name="startDate"
          rules={{ required: true }}
          render={({ field }) => (
            <I18nProvider locale="ru-RU">
              <DatePicker
                variant="bordered"
                isRequired
                label="Дата начала договора"
                onChange={(date) => field.onChange(date?.toString())}
                className="w-full"
                value={
                  field.value
                    ? parseDate(normalizeDate(field.value))
                    : undefined
                }
                isInvalid={!!form.formState.errors["startDate"]}
                errorMessage={
                  form.formState.errors["startDate"]?.message?.toString() || ""
                }
              />
            </I18nProvider>
          )}
        />

        <Controller
          control={form.control}
          name="endDate"
          rules={{ required: true }}
          render={({ field }) => (
            <I18nProvider locale="ru-RU">
              <DatePicker
                variant="bordered"
                isRequired
                label="Дата окончания договора"
                onChange={(date) => field.onChange(date?.toString())}
                className="w-full"
                value={
                  field.value
                    ? parseDate(normalizeDate(field.value))
                    : undefined
                }
                isInvalid={!!form.formState.errors["endDate"]}
                errorMessage={
                  form.formState.errors["endDate"]?.message?.toString() || ""
                }
              />
            </I18nProvider>
          )}
        />

        <div className="flex gap-2 justify-end mt-4">
          {isEdit && onDelete && (
            <Button
              color="danger"
              variant="light"
              onPress={onDelete}
              isDisabled={form.formState.isSubmitting}>
              Удалить
            </Button>
          )}
          <Button
            color="primary"
            type="submit"
            isDisabled={form.formState.isSubmitting}>
            {isEdit ? "Сохранить" : "Далее"}
          </Button>
        </div>
      </div>
    </form>
  );
}
